import React, { useCallback, useMemo, useState } from "react";
import {
	HamburgerIcon,
	DarkWhiteLogoIcon,
	LogoLightNavIcon,
	MoonIcon,
	SunIcon,
} from "@assets/icons";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { useTheme } from "hooks/useTheme";
import { scrollToSection } from "@utils/scrollToSection";
import { motion, AnimatePresence } from "framer-motion";
import { ROUTES } from "constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@components/common/LanguageSwitcher/LanguageSwitcher";
import { useSelector } from "react-redux";
import { RootState } from "store/store";

interface MobileNavbarProps {
	onOpenLogin: () => void;
	onOpenRegister: () => void;
}

const MobileNavbar: React.FC<MobileNavbarProps> = ({
	onOpenLogin,
	onOpenRegister,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [menuOpen, setMenuOpen] = useState(false);
	const { theme, toggleTheme } = useTheme();

	const isAuthenticated = useSelector(
		(state: RootState) => state.auth.isAuthenticated,
	);

	const goOrScroll = useCallback(
		(section?: string) => {
			if (location.pathname === "/") {
				if (section) scrollToSection(section);
				else window.scrollTo({ top: 0, behavior: "smooth" });
			} else {
				navigate("/", { state: { scrollTo: section } });
			}
			setMenuOpen(false);
		},
		[location.pathname, navigate],
	);

	const items = useMemo(
		() => [
			{ key: "home", section: undefined, label: t("landing.nav.home") },
			{
				key: "about",
				section: "achievements",
				label: t("landing.nav.about"),
			},
			{ key: "news", section: "news", label: t("landing.nav.news") },
			{
				key: "partners",
				section: "partners",
				label: t("landing.nav.partners"),
			},
			{ key: "faq", section: "faq", label: t("landing.nav.faq") },
		],
		[t],
	);

	return (
		<>
			<header className="bg-light-bgColor dark:bg-dark-bgColor p-4 flex items-center justify-between relative z-50">
				<SvgIcon
					Icon={theme === "light" ? LogoLightNavIcon : DarkWhiteLogoIcon}
					className="w-[92px] h-[32px]"
				/>

				<button
					type="button"
					onClick={() => setMenuOpen((p) => !p)}
					aria-label="Menu"
				>
					<SvgIcon
						Icon={HamburgerIcon}
						className="dark:stroke-light-whiteText stroke-dark-whiteText"
						width={24}
						height={24}
					/>
				</button>
			</header>

			<AnimatePresence>
				{menuOpen && (
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.25, ease: "easeInOut" }}
						className="absolute top-[72px] left-4 right-4 z-[99] space-y-4 p-4 rounded-lg bg-white dark:bg-dark-bgColor border border-light-stroke dark:border-dark-stroke"
					>
						{/* ✅ Links */}
						{items.map((it) => (
							<P
								key={it.key}
								size="t1"
								type="semibold"
								className="cursor-pointer"
								onClick={() => goOrScroll(it.section)}
							>
								{it.label}
							</P>
						))}

						<P
							size="t1"
							type="semibold"
							className="cursor-pointer border-b border-light-strokeLanding dark:border-dark-strokeLanding pb-2"
							onClick={() => {
								navigate(ROUTES.HOW_TO_USE);
								setMenuOpen(false);
							}}
						>
							{t("landing.nav.howToUse")}
						</P>

						<div className="flex items-center justify-between">
							<LanguageSwitcher variant="landing" mode="full" />
						</div>

						{/* ✅ Auth actions */}
						{isAuthenticated ? (
							<div
								className="bg-light-mainColor dark:bg-dark-mainColor py-2.5 px-4 rounded-full cursor-pointer w-max"
								onClick={() => {
									navigate(ROUTES.MAIN);
									setMenuOpen(false);
								}}
							>
								<P
									size="t1"
									type="semibold"
									className="text-light-bgColor dark:text-dark-bgColor"
								>
									{t("landing.nav.platform")}
								</P>
							</div>
						) : (
							<>
								<div
									onClick={() => {
										onOpenLogin();
										setMenuOpen(false);
									}}
									className="flex items-center gap-2 cursor-pointer w-max"
								>
									<P
										size="t1"
										type="semibold"
										className="text-light-mainColor dark:text-dark-mainColor"
									>
										{t("auth.login.login")}
									</P>
								</div>

								<div
									className="flex items-center gap-2 cursor-pointer w-max"
									onClick={() => {
										onOpenRegister();
										setMenuOpen(false);
									}}
								>
									<P
										size="t1"
										type="semibold"
										className="text-light-mainColor dark:text-dark-mainColor text-center"
									>
										{t("auth.register.register")}
									</P>
								</div>
							</>
						)}

						{/* ✅ Theme toggle */}
						<div
							className="bg-light-mainColor dark:bg-dark-mainColor p-2.5 rounded-full cursor-pointer w-max"
							onClick={toggleTheme}
							aria-label={t("common.toggleTheme")}
						>
							<SvgIcon
								className="lg:w-6 lg:h-6 minPhone:w-4 minPhone:h-4"
								Icon={theme === "light" ? MoonIcon : SunIcon}
							/>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default React.memo(MobileNavbar);
